// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

import type { Belt, GbnfDrop, GbnfResult, SchemaNode, TargetId } from '../types';
import { walkSchema } from '../lint/walk';

/**
 * The `post-parse-checks.ts` generator.
 *
 * IDEAS.md justified this file with a claim that turned out to be false: that OpenAI strict
 * "silently lets through" `pattern`, `minLength`, `minimum` and `uniqueItems`. It does not —
 * `pattern` and `minimum` have been on its supported list since May 2025, and `uniqueItems` returns
 * a 400. So the file rests on four different, defensible gaps instead (RESEARCH §0.1, §5):
 *
 *   (a) `strict` omitted or silently falling back. The Responses API normalises a schema into strict
 *       mode when it can and falls back to non-strict best-effort when it cannot, signalling that
 *       only by echoing `strict: false` back on the tool. Chat Completions is non-strict by default.
 *   (b) Keywords the GBNF path drops — computed PER NODE from sibling keywords, which is why this
 *       file and the grammar come out of one walk and cannot drift apart.
 *   (c) `oneOf` exclusivity, relaxed to `anyOf` by every constrained decoder in the matrix, and
 *       cross-field invariants JSON Schema cannot express at all.
 *   (d) Refusal and truncation, which OpenAI's own docs admit can produce non-conforming output.
 *
 * Plus, on Anthropic, the one documented case in the whole matrix of a provider returning a
 * schema-violating value with a 200 and no signal: enum and const capitalisation.
 *
 * The emitted file is a DECLARATIVE TABLE plus a small generic runtime, not a pile of hand-unrolled
 * accessors. That keeps it short, keeps it free of the `TODO` comments a code reviewer would rightly
 * reject, and means an array anywhere in the path fans out correctly instead of being ignored.
 */

export interface CheckPlan {
  readonly tool: string;
  readonly pointer: string;
  readonly keyword: string;
  readonly reason: string;
  readonly value: unknown;
}

type Seg = { readonly k: 'key'; readonly name: string } | { readonly k: 'each' };

const ASSERTABLE = new Set([
  'pattern', 'minLength', 'maxLength', 'minimum', 'maximum', 'exclusiveMinimum', 'exclusiveMaximum',
  'multipleOf', 'minItems', 'maxItems', 'uniqueItems', 'enum', 'const',
]);

/**
 * Translate a SCHEMA pointer into a DATA path.
 *
 * Returns null when the node lives inside a union branch — a value under `anyOf[1]` has no single
 * address in the data, and emitting a wrong accessor is worse than emitting none. Those become a
 * comment in the file instead.
 */
export function pointerToPath(pointer: string): Seg[] | null {
  const tokens = pointer
    .split('/')
    .filter(Boolean)
    .map((t) => t.replace(/~1/g, '/').replace(/~0/g, '~'));

  const path: Seg[] = [];
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i]!;
    if (t === 'properties') {
      const name = tokens[++i];
      if (name === undefined) return null;
      path.push({ k: 'key', name });
    } else if (t === 'items' || t === 'prefixItems' || t === 'additionalItems' || t === 'contains') {
      if (/^\d+$/.test(tokens[i + 1] ?? '')) i++;
      path.push({ k: 'each' });
    } else {
      // anyOf / oneOf / allOf / $defs / definitions / patternProperties / if / then / else …
      return null;
    }
  }
  return path;
}

export function planFromDrops(belt: Belt, gbnf: Readonly<Record<string, GbnfResult>>): CheckPlan[] {
  const plans: CheckPlan[] = [];
  const byTool = new Map(belt.tools.map((t) => [t.name, t.schema]));

  for (const [toolName, result] of Object.entries(gbnf)) {
    const schema = byTool.get(toolName);
    if (!schema) continue;
    for (const drop of result.drops) {
      if (!ASSERTABLE.has(drop.keyword)) continue;
      const value = valueAt(schema, drop);
      if (value === undefined) continue;
      plans.push({ tool: toolName, pointer: drop.pointer, keyword: drop.keyword, reason: drop.reason, value });
    }
  }
  return plans;
}

function valueAt(schema: SchemaNode, drop: GbnfDrop): unknown {
  let found: unknown;
  walkSchema(schema, ({ node, pointer }) => {
    if (pointer === drop.pointer && drop.keyword in node) found = node[drop.keyword];
  });
  return found;
}

export function planOneOf(belt: Belt): CheckPlan[] {
  const out: CheckPlan[] = [];
  for (const tool of belt.tools) {
    if (!tool.schema) continue;
    walkSchema(tool.schema, ({ node, pointer }) => {
      if (Array.isArray(node['oneOf'])) {
        out.push({
          tool: tool.name,
          pointer,
          keyword: 'oneOf',
          reason: 'every constrained decoder compiles `oneOf` as `anyOf`, so exclusivity is never enforced',
          value: node['oneOf'].length,
        });
      }
    });
  }
  return out;
}

export function planEnums(belt: Belt): CheckPlan[] {
  const out: CheckPlan[] = [];
  for (const tool of belt.tools) {
    if (!tool.schema) continue;
    walkSchema(tool.schema, ({ node, pointer }) => {
      const en = node['enum'];
      if (Array.isArray(en) && en.some((v) => typeof v === 'string')) {
        out.push({
          tool: tool.name,
          pointer,
          keyword: 'enum-case',
          reason: 'Anthropic does not guarantee the capitalisation of the enum values it returns',
          value: en,
        });
      }
      if (typeof node['const'] === 'string') {
        out.push({
          tool: tool.name,
          pointer,
          keyword: 'const-case',
          reason: 'Anthropic does not guarantee the capitalisation of the const value it returns',
          value: node['const'],
        });
      }
    });
  }
  return out;
}

export interface GeneratedChecks {
  readonly code: string;
  /** How many mechanical assertions the file carries. */
  readonly count: number;
  /** How many findings could only be emitted as a comment. */
  readonly manual: number;
}

export function generateChecks(
  belt: Belt,
  target: TargetId,
  plans: readonly CheckPlan[],
  extras: {
    readonly strictMissing: readonly string[];
    readonly oneOf: readonly CheckPlan[];
    readonly enums: readonly CheckPlan[];
  },
): GeneratedChecks {
  const rows: string[] = [];
  const manualLines: string[] = [];
  let manual = 0;

  const emit = (p: CheckPlan, kind: string): void => {
    const path = pointerToPath(p.pointer);
    if (path === null) {
      manual++;
      manualLines.push(
        `//   ${p.tool} · ${p.pointer || '(root)'} · ${kind} — inside a union branch, so it has no single ` +
          `address in the arguments. Check it by hand: ${p.reason}.`,
      );
      return;
    }
    rows.push(
      `  { tool: ${JSON.stringify(p.tool)}, path: ${JSON.stringify(path)}, ` +
        `kind: ${JSON.stringify(kind)}, value: ${JSON.stringify(p.value)}, ` +
        `why: ${JSON.stringify(p.reason)} },`,
    );
  };

  for (const p of plans) emit(p, p.keyword);
  for (const p of extras.oneOf) {
    manual++;
    manualLines.push(
      `//   ${p.tool} · ${p.pointer || '(root)'} · oneOf with ${String(p.value)} branches — assert that ` +
        'EXACTLY ONE matches. The decoder only guaranteed at least one.',
    );
  }
  for (const p of extras.enums) emit(p, p.keyword);

  const header: string[] = [];
  header.push('/**');
  header.push(' * post-parse-checks.ts — generated by Toolwright (toolwright.benrichardson.dev)');
  header.push(' *');
  header.push(' * These are the constraints in your tool schemas that the model was NOT physically prevented');
  header.push(' * from violating. Run them on the arguments you receive, after JSON.parse and before you act.');
  header.push(' *');
  header.push(` * Target: ${target}`);
  header.push(
    ` * Belt: ${belt.tools.length} tool${belt.tools.length === 1 ? '' : 's'}, read in the ${belt.dialect} shape.`,
  );
  header.push(' *');
  header.push(' * No imports and no dependencies, on purpose — paste it in.');
  header.push(' */');
  header.push('');

  if (extras.strictMissing.length > 0) {
    header.push('/*');
    header.push(' * READ THIS FIRST.');
    header.push(' *');
    header.push(
      ` * ${extras.strictMissing.length} of your ${belt.tools.length} tools do not set \`strict: true\`:`,
    );
    for (const n of extras.strictMissing.slice(0, 20)) header.push(` *   - ${n}`);
    if (extras.strictMissing.length > 20) header.push(` *   … and ${extras.strictMissing.length - 20} more`);
    header.push(' *');
    header.push(' * On Chat Completions, non-strict is the default and NOTHING in your schema is enforced. On');
    header.push(' * the Responses API a schema is normalised into strict mode when it can be and falls back to');
    header.push(' * non-strict best-effort when it cannot, signalled only by `strict: false` coming back on the');
    header.push(' * tool. For these tools every check below is load-bearing rather than belt-and-braces.');
    header.push(' */');
    header.push('');
  }

  const body = `${RUNTIME}

const CHECKS: readonly Check[] = [
${rows.join('\n') || '  // Nothing mechanically checkable was dropped for this target.'}
];

/** Every violation found in \`args\` for \`tool\`. Empty means nothing this file can see is wrong. */
export function checkToolArgs(tool: string, args: unknown): Violation[] {
  const out: Violation[] = [];
  for (const c of CHECKS) {
    if (c.tool !== tool) continue;
    for (const { value, path } of resolve(args, c.path)) {
      const message = verify(c, value);
      if (message) out.push({ tool, path, keyword: c.kind, message, why: c.why });
    }
  }
  return out;
}

/** Throwing wrapper, for callers who would rather not branch. */
export function assertToolArgs(tool: string, args: unknown): void {
  const v = checkToolArgs(tool, args);
  if (v.length > 0) {
    throw new PostParseViolation(
      tool,
      v.map((x) => \`\${x.path}: \${x.message}\`).join('; '),
      v,
    );
  }
}
${manualLines.length > 0 ? `\n// ── check these by hand ────────────────────────────────────────────────────\n//\n${manualLines.join('\n')}\n` : ''}
${TRAILER}`;

  return { code: header.join('\n') + body, count: rows.length, manual };
}

const RUNTIME = `export interface Violation {
  readonly tool: string;
  readonly path: string;
  readonly keyword: string;
  readonly message: string;
  /** Why this constraint had to be re-checked here rather than trusted. */
  readonly why: string;
}

export class PostParseViolation extends Error {
  constructor(
    readonly tool: string,
    message: string,
    readonly violations: readonly Violation[],
  ) {
    super(\`\${tool}: \${message}\`);
    this.name = 'PostParseViolation';
  }
}

type Seg = { k: 'key'; name: string } | { k: 'each' };

interface Check {
  readonly tool: string;
  readonly path: readonly Seg[];
  readonly kind: string;
  readonly value: unknown;
  readonly why: string;
}

/**
 * Walk a path into the arguments, fanning out at every array.
 *
 * A missing key yields nothing rather than \`undefined\`: an optional argument the model omitted is
 * not a violation of a \`minLength\`, and reporting one would train you to ignore this file.
 */
function resolve(root: unknown, path: readonly Seg[]): { value: unknown; path: string }[] {
  let frontier: { value: unknown; path: string }[] = [{ value: root, path: '' }];
  for (const seg of path) {
    const next: { value: unknown; path: string }[] = [];
    for (const cur of frontier) {
      if (seg.k === 'key') {
        if (typeof cur.value !== 'object' || cur.value === null || Array.isArray(cur.value)) continue;
        const obj = cur.value as Record<string, unknown>;
        if (!(seg.name in obj)) continue;
        next.push({ value: obj[seg.name], path: cur.path + '.' + seg.name });
      } else {
        if (!Array.isArray(cur.value)) continue;
        cur.value.forEach((v, i) => next.push({ value: v, path: cur.path + '[' + i + ']' }));
      }
    }
    frontier = next;
  }
  return frontier.filter((f) => f.value !== undefined && f.value !== null);
}

function verify(c: Check, v: unknown): string | null {
  switch (c.kind) {
    case 'pattern':
      // NOTE: OpenAI does not document which regex flavour \`pattern\` uses, while stating elsewhere
      // that sampling is constrained by LLGuidance with Rust regex-crate syntax — no backreferences,
      // no lookaround. JavaScript has both. A pattern that compiles here may not be the pattern the
      // model was actually constrained by.
      return typeof v === 'string' && !new RegExp(String(c.value), 'u').test(v)
        ? \`"\${v}" does not match \${JSON.stringify(c.value)}\`
        : null;
    case 'minLength':
      return typeof v === 'string' && v.length < Number(c.value)
        ? \`length \${v.length} is under the minimum of \${String(c.value)}\`
        : null;
    case 'maxLength':
      return typeof v === 'string' && v.length > Number(c.value)
        ? \`length \${v.length} is over the maximum of \${String(c.value)}\`
        : null;
    case 'minimum':
      return typeof v === 'number' && v < Number(c.value) ? \`\${v} is under the minimum of \${String(c.value)}\` : null;
    case 'maximum':
      return typeof v === 'number' && v > Number(c.value) ? \`\${v} is over the maximum of \${String(c.value)}\` : null;
    case 'exclusiveMinimum':
      return typeof v === 'number' && v <= Number(c.value) ? \`\${v} must be greater than \${String(c.value)}\` : null;
    case 'exclusiveMaximum':
      return typeof v === 'number' && v >= Number(c.value) ? \`\${v} must be less than \${String(c.value)}\` : null;
    case 'multipleOf': {
      if (typeof v !== 'number') return null;
      const m = Number(c.value);
      // Float remainders are unreliable; scale to integers before testing.
      const scale = Math.pow(10, Math.max(decimals(v), decimals(m)));
      return Math.round(v * scale) % Math.round(m * scale) !== 0 ? \`\${v} is not a multiple of \${m}\` : null;
    }
    case 'minItems':
      return Array.isArray(v) && v.length < Number(c.value)
        ? \`\${v.length} items, fewer than the minimum of \${String(c.value)}\`
        : null;
    case 'maxItems':
      return Array.isArray(v) && v.length > Number(c.value)
        ? \`\${v.length} items, more than the maximum of \${String(c.value)}\`
        : null;
    case 'uniqueItems': {
      if (!Array.isArray(v)) return null;
      const seen = new Set(v.map((x) => JSON.stringify(x)));
      return seen.size !== v.length ? \`\${v.length - seen.size} duplicate item(s)\` : null;
    }
    case 'enum':
      return Array.isArray(c.value) && !(c.value as unknown[]).some((x) => x === v)
        ? \`\${JSON.stringify(v)} is not one of \${JSON.stringify(c.value)}\`
        : null;
    case 'const':
      return v !== c.value ? \`\${JSON.stringify(v)} is not \${JSON.stringify(c.value)}\` : null;
    case 'enum-case': {
      // Anthropic may return a value differing from the schema only in capitalisation, with a 200
      // and no special stop_reason. Accept it, but tell the caller to normalise.
      if (typeof v !== 'string' || !Array.isArray(c.value)) return null;
      const members = c.value as unknown[];
      if (members.some((x) => x === v)) return null;
      const ci = members.find((x) => typeof x === 'string' && x.toLowerCase() === v.toLowerCase());
      return ci !== undefined
        ? \`"\${v}" differs from the schema value "\${String(ci)}" only in capitalisation — normalise before comparing\`
        : \`\${JSON.stringify(v)} is not one of \${JSON.stringify(members)}\`;
    }
    case 'const-case': {
      if (typeof v !== 'string' || typeof c.value !== 'string') return null;
      if (v === c.value) return null;
      return v.toLowerCase() === c.value.toLowerCase()
        ? \`"\${v}" differs from "\${c.value}" only in capitalisation — normalise before comparing\`
        : \`"\${v}" is not "\${c.value}"\`;
    }
    default:
      return null;
  }
}

function decimals(n: number): number {
  const s = String(n);
  const i = s.indexOf('.');
  return i === -1 ? 0 : s.length - i - 1;
}`;

const TRAILER = `
// ── what this file cannot check ────────────────────────────────────────────
//
// 1. Cross-field and business invariants. "\`end\` must be after \`start\`", "this id must exist in our
//    database" — JSON Schema cannot express them, so no decoder can enforce them and no generator
//    can write them for you. They are yours.
// 2. Refusals and truncation. A response can be cut off at the token limit or come back as a
//    refusal, and in both cases the arguments will not match the schema however strict it is. Check
//    \`incomplete_details.reason === "max_output_tokens"\` and the refusal content item BEFORE you
//    parse anything.
// 3. Whether the model understood the tool at all. That is a prompt problem, not a schema problem.
`;
